/** 출고/입고 전표 라인 테이블의 키보드 내비게이션 결정 로직 (부작용 없음).
 *
 * 컴포넌트(OrderCrudTab)의 rowKeyDown 은 DOM 상태(커서 위치·텍스트 유무)만
 * 읽어 이 순수 함수에 넘기고, 반환된 액션을 실제 focus/addLine/removeLine 으로
 * 실행한다. 결정부를 분리해 단위 테스트가 가능하게 함.
 */

// 라인 내 필드 순서 — ← → 이동, ↑ ↓ 행 이동에 사용.
export const FIELDS = ['item-trigger', 'spec', 'unit', 'qty', 'unit-price'] as const
export type FieldName = typeof FIELDS[number]

/** 포커스를 옮길 대상. */
export type FocusTarget =
  | { kind: 'field'; index: number; field: FieldName }  // idx 번 라인의 field 로
  | { kind: 'addLine'; focus: FieldName }               // 새 라인 추가 후 focus
  | { kind: 'addLineBtn' }                               // '라인 추가' 버튼
  | { kind: 'saveBtn' }                                  // '저장' 버튼

/** 키 입력에 대한 결정. type === 'none' 이면 preventDefault 하지 말고 native 처리. */
export type RowNavAction =
  | { type: 'none' }
  | { type: 'focus'; target: FocusTarget }
  | { type: 'deleteLine'; index: number; then: FocusTarget }

export interface RowNavInput {
  key: string
  ctrlKey: boolean
  metaKey: boolean
  shiftKey: boolean
  field: FieldName
  index: number         // 현재 라인 인덱스
  linesLength: number   // 전체 라인 수
  atStart: boolean      // 텍스트 커서가 맨 앞(← 이동 허용). 비텍스트 필드는 true.
  atEnd: boolean        // 텍스트 커서가 맨 끝(→ 이동 허용). 비텍스트 필드는 true.
  isTextWithContent: boolean  // Delete 시 native 문자삭제 유지 판단용.
}

export function decideRowNav(inp: RowNavInput): RowNavAction {
  const { key, ctrlKey, metaKey, shiftKey, field, index: i, linesLength } = inp
  const isLast = i === linesLength - 1
  const cur = FIELDS.indexOf(field)

  // Delete: 라인 삭제 — 단, 텍스트 input에 내용이 있으면 native(문자 삭제) 유지.
  if (key === 'Delete' && !ctrlKey && !metaKey && !shiftKey) {
    if (inp.isTextWithContent) return { type: 'none' }
    const oldLen = linesLength
    let then: FocusTarget
    if (oldLen <= 1) then = { kind: 'addLineBtn' }             // 마지막 한 줄 삭제 → 추가 버튼
    else if (i >= oldLen - 1) then = { kind: 'field', index: oldLen - 2, field }  // 마지막 라인 → 새 마지막
    else then = { kind: 'field', index: i, field }             // 다음 라인이 슬라이드 업
    return { type: 'deleteLine', index: i, then }
  }

  // Enter: qty → unit-price → 다음행 item-trigger (마지막 행이면 새 라인)
  if (key === 'Enter' && !ctrlKey && !metaKey) {
    if (field === 'qty') return { type: 'focus', target: { kind: 'field', index: i, field: 'unit-price' } }
    if (field === 'unit-price') {
      return isLast
        ? { type: 'focus', target: { kind: 'addLine', focus: 'item-trigger' } }
        : { type: 'focus', target: { kind: 'field', index: i + 1, field: 'item-trigger' } }
    }
    return { type: 'none' }
  }

  if (key === 'ArrowRight' && inp.atEnd) {
    const next = FIELDS[cur + 1]
    if (next) return { type: 'focus', target: { kind: 'field', index: i, field: next } }
    if (isLast && field === 'unit-price') return { type: 'focus', target: { kind: 'saveBtn' } }
    return { type: 'none' }
  }
  if (key === 'ArrowLeft' && inp.atStart) {
    const prev = FIELDS[cur - 1]
    if (prev) return { type: 'focus', target: { kind: 'field', index: i, field: prev } }
    return { type: 'none' }
  }
  if (key === 'ArrowUp') {
    if (i > 0) return { type: 'focus', target: { kind: 'field', index: i - 1, field } }
    return { type: 'focus', target: { kind: 'addLineBtn' } }   // 첫 라인 위쪽 → 추가 버튼
  }
  if (key === 'ArrowDown') {
    if (isLast) {
      // 마지막 행 ↓ → 새 라인. 수량에서 내리면 다음 품목을 바로 이어 입력하도록
      // 새 행 '품명'으로, 그 외 필드는 같은 필드로.
      return { type: 'focus', target: { kind: 'addLine', focus: field === 'qty' ? 'item-trigger' : field } }
    }
    return { type: 'focus', target: { kind: 'field', index: i + 1, field } }  // 아래 행 같은 필드
  }

  return { type: 'none' }
}
